import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { delay, Observable, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  GetNotificationsParams,
  NotificationListResponse,
  NotificationMarkAllReadResponse,
  NotificationMarkReadResponse,
  NotificationUnreadCountResponse,
} from '../models/notification.models';

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly baseUrl = `${environment.BASE_URL_API}user/notifications`;
  
  /** When true, methods return mock data instead of calling the API. */
  useMockData = environment.useMockData ?? false;

  constructor(private http: HttpClient) {}

  // ── List ───────────────────────────────────────────────────────────────────

  getNotifications(params?: GetNotificationsParams): Observable<NotificationListResponse> {
    let httpParams = new HttpParams();
    if (params?.unread_only != null) {
      httpParams = httpParams.set('unread_only', String(params.unread_only));
    }
    if (params?.limit != null) {
      httpParams = httpParams.set('limit', String(params.limit));
    }
    if (params?.offset != null) {
      httpParams = httpParams.set('offset', String(params.offset));
    }

    if (this.useMockData) {
      const mockResponse: NotificationListResponse = {
        data: [
          {
            id: 1,
            source_module: 'smart_inbox',
            event_type: 'comment_mention',
            title: 'You were mentioned in a conversation',
            payload: {
              thread_id: 101,
              comment_id: 501,
              author_id: 12,
              thread_subject: 'Pricing follow-up',
              snippet: 'Can you confirm final discount approval?',
            },
            is_read: false,
            created_at: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
          },
          {
            id: 2,
            source_module: 'smart_inbox',
            event_type: 'thread_assignment',
            title: 'A thread was assigned to you',
            payload: {
              author_id: 8,
              thread_ids: [202, 203],
            },
            is_read: false,
            created_at: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
          },
          {
            id: 3,
            source_module: 'smart_inbox',
            event_type: 'order_created',
            title: 'A new order was created',
            payload: {
              thread_id: 304,
              thread_subject: 'Order #304',
            },
            is_read: true,
            created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          },
        ],
        object: null,
        metadata: {
          total: 3,
          limit: params?.limit ?? 10,
          offset: params?.offset ?? 0,
          unread_only: params?.unread_only ?? false,
        },
      };

      return of(mockResponse).pipe(delay(1000));
    }

    return this.http.get<NotificationListResponse>(this.baseUrl, { params: httpParams });
  }

  // ── Unread count ───────────────────────────────────────────────────────────

  getUnreadCount(): Observable<NotificationUnreadCountResponse> {
    return this.http.get<NotificationUnreadCountResponse>(`${this.baseUrl}/unread-count`);
  }

  // ── Mark as read ───────────────────────────────────────────────────────────

  markAsRead(id: number): Observable<NotificationMarkReadResponse> {
    return this.http.patch<NotificationMarkReadResponse>(`${this.baseUrl}/${id}/read`, {});
  }

  // ── Mark all as read ───────────────────────────────────────────────────────

  markAllAsRead(): Observable<NotificationMarkAllReadResponse> {
    return this.http.patch<NotificationMarkAllReadResponse>(`${this.baseUrl}/read-all`, {});
  }
}
